<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-contact-reasons-detail-metrics-chart" v-loading="loading || parentLoading">
    <table border="0">
    <tr>
      <td align="right" width="250"><br></td>
      <td width="*" align="center">
        <div class="title">{{title}}</div>
      </td>
      <td align="right" width="250">
        <div class="wy-chart-grouping">
          <el-select
            v-model="selectedDateRange"
            collapse-tags
            size="mini"
            style="margin-left: 5px;"
            popper-class="line-chart-dropdown"
            @change="changeDateRange"
            placeholder="Select range">
            <el-option
              v-for="(item) in dateRangeTypes"
              :disabled="item.disabled"
              :key="item.type"
              :label="item.label"
              :value="item.type">
            </el-option>
          </el-select>
        </div>
      </td>
    </tr>
    </table>
    <div ref="chart" class="metrics-chart" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyChartModule from '../../library/wy-chart-module.js';
import moment from 'moment';

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-metrics-chart',
  props: ['parent', 'activeTab', 'selectedChart', 'viewType', 'id', 'title', 'column', 'data', 'format', 'suffix', 'parentLoading'],

  data() {
    return {
      loading: false,
      wyChart: null,
      selectedDateRange: null,
      dataList: null
    }
  },

  watch: {
    activeTab(type) {      
      if(type == 'metrics') {
        this.buildChart();
      }
      else {
        this.$refs.chart.innerHTML = '';
      }
    },

    selectedChart() {
      this.changeDateRange(this.viewType);
    },

    viewType: function (val) {
      this.changeDateRange(val);
    },
    data: function () {
      this.initData(this.viewType);
    }
  },

  computed: {
    ...mapGetters(['companyConfig']),

    dateRangeTypes() {
      return this.wyChart.getDateRangeTypes();
    },
  },

  methods: {
    changeDateRange(type) {
      this.loading = true;        
      setTimeout(() => {
        this.initData(type);
        this.buildChart();
        this.loading = false;
      }, 300)
      this.parent.handleViewTypeChange(type);
    },

    buildChart() {
      if(this.$refs.chart && this.dataList) {
        let dateRangeType = this.wyChart.getDateRangeType();
        let median = this.wyChart.prepareRegressionData(this.dataList, {format: this.format});
        let monthView = dateRangeType.type == 'month'
        let yearView = dateRangeType.type == 'year'


        let traceData = {
          name: this.title,
          type: "scatter",
          mode: "lines+markers",
          cliponaxis: false,
          //marker: {size: median.data.markerSize},
          marker: {color: median.data.markerColor, size: 8},
          line: {
            //shape: 'spline',
            color: '#214586'
          },
          x: median.data.x,
          y: median.data.y,
          text: median.data.medianTexts,
          hovertext: median.data.texts,
          hovertemplate: this.wyChart.hoverTemplateMedian(this.suffix),
        };

        if(median.data.y.length == 1) {
          // print marker if only one data item
          traceData.marker = {size: 8};
        }

        let traceMedianLine = {
          name: 'Median',
          type: 'scatter',
          mode: "lines",
          hoverinfo: 'none',
          x: median.medianData.x,
          y: median.medianData.y,
          line: {
            color: '#444',
            width: 1
          },
        };

        let traceRegression = {
          name: 'Trend',
          type: 'scatter',
          mode: "lines",
          x: median.regressionData.xValues,
          y: median.regressionData.yValues,
          hoverinfo: 'none',
          fill: 'none',
          line: {
            dash: 'dot',
            color: '#000',
            width: 1
          },          
        };

        let traces = [traceData, traceMedianLine, traceRegression];

        let layout = {
          autosize: true,
          //width: 408,
          height: 290,          
          dragmode: false, //'pan'
          separators: '.',
          showlegend: true,
          plot_bgcolor: 'white',
          hoverlabel: {
            namelength: 50,
            font: {
              size: 11
            },
            bordercolor: '#FCFEFF'
          },
          legend: {
            orientation: 'h',
            x: 0.37,
            y: -0.2
          },
          margin: { l: 70, r: 40, t: 20, b: 40 },
          
          xaxis: {
            type: 'date',
            tickformat: dateRangeType.tickformat,
            tickvals: monthView ? median.data.x.slice(1) : yearView ? median.data.x : null,
            ticktext: monthView || yearView ? median.data.xTexts: null,
            tickangle: 0,
            autorange: true,
            fixedrange: true,
            showgrid: false,
            zeroline: false,
            automargin: true,
            showline: true,
            linecolor: '#dedede',
            position: 0,
            tickcolor: 'transparent',
          },
          yaxis: {
            type: 'linear',
            autorange: true,
            //rangemode: 'tozero',
            separatethousands: true,
            showgrid: true,
            showline: true,
            zeroline: false,
            linecolor: '#dedede',
            ticksuffix: (this.suffix + ' '),
            tickformat: (WyUtils[this.format] ? '' : (this.format == 'time' ? '' : Number(this.format))),
            fixedrange: true,
          }
        }        
        this.wyChart.finalizeRegressionChart(traces, layout, this.format, {});

        let config = {responsive: true, displayModeBar: false, scrollZoom: false, showTips: false};
        // eslint-disable-next-line no-undef
        Plotly.newPlot(this.$refs.chart, traces, layout, config);

        this.$refs.chart.on('plotly_hover', (data) => {          
          try {
            let dataPoint = data.points[0];
            if(dataPoint && dataPoint.data && dataPoint.data.marker) {              
              let pn = dataPoint.pointNumber;
              // let sizes = dataPoint.data.marker.size;
              // sizes[pn] = 8;
              // let update = {'marker': {size: sizes}};
              let colors = dataPoint.data.marker.color;
              colors[pn] = "#214586";
              let update = {'marker': {color: colors}};
              // eslint-disable-next-line no-undef
              Plotly.relayout(this.$refs.chart, update, 0);
            }
          }
          catch {
            // catch error
          }
        });

        // mouse out on point
        this.$refs.chart.on('plotly_unhover', (data) => {
          try {
            let dataPoint = data.points[0];
            if(dataPoint && dataPoint.data && dataPoint.data.marker) {
              let pn = dataPoint.pointNumber;
              // let sizes = dataPoint.data.marker.size;
              // sizes[pn] = 0;
              // let update = {'marker': {size: sizes}};
              let colors = dataPoint.data.marker.color;            
              colors[pn] = "transparent";
              let update = {'marker': {color: colors}};
              // eslint-disable-next-line no-undef
              Plotly.relayout(this.$refs.chart, update, 0);
            }
          }
          catch {
            // catch error
          }
        });
      }
    },

    initData(type) {
      if(this.data && this.data.trend) {
        let dataItems;
        if(this.column == 'sumHandlingTime') {
          dataItems = this.data.trend.filter(el => el.sumHandlingNotNegative >= 0);
        }
        else if(this.column == 'sumResponseTime') {
          dataItems = this.data.trend.filter(el => el.sumResponseNotNegative >= 0);
        }
        else {
          dataItems = this.data.trend;
        }

        if(dataItems.length > 0) {
          // prepare date range
          let dateStart = moment(dataItems[0].trendDate,'YYYY-MM-DD');
          let dateEnd = moment(dataItems[dataItems.length-1].trendDate,'YYYY-MM-DD');
          this.wyChart.init(type, dateStart, dateEnd, null, (this.companyConfig.default || {}).selectedViewBy || "week");
          this.selectedDateRange = this.wyChart.selectedDateRange;        

          // prepare date types
          dataItems.forEach(el => {
            this.wyChart.prepareGroupDates(el, el.trendDate);
          })

          // group by type
          this.dataList = this.wyChart.groupBy(dataItems, (items) => {
            let value = 0;
            
            let sumConversations = 0;
            if(this.column == 'sumHandlingTime') {
              // eslint-disable-next-line no-undef
              sumConversations = _.sumBy(items, 'sumHandlingNotNegative');
            }
            else if(this.column == 'sumResponseTime') {
              // eslint-disable-next-line no-undef
              sumConversations = _.sumBy(items, 'sumResponseNotNegative');
            }
            else {
              // eslint-disable-next-line no-undef
              sumConversations = _.sumBy(items, 'sumConversations');
            }
            if(sumConversations == 0) sumConversations = 1;
            
            if(this.suffix == 'm') {
              // eslint-disable-next-line no-undef
              value = _.sumBy(items, this.column) / sumConversations
            }
            else if(this.suffix == '%') {
              // eslint-disable-next-line no-undef
              value = _.sumBy(items, this.column) / items.length
            }
            else {
              // eslint-disable-next-line no-undef
              value = _.sumBy(items, this.column)
            }

            return {
              date: items[0].day,
              value: value,
            }
          });
        }
        else {
          this.dataList = [];
        }
      }
    },

    generateMockData() {
      let today = moment()
      let trend = [];
      for(var i=1555; i > 0; i--) {
        let date = today.subtract(1, 'd')
        trend.push({
          "topicDisplayName": "file claim",
          "trendDate": date.format('YYYY-MM-D'),
          "sumPositive": 1,
          "sumNegative": 0,
          "sumNeutral": 0,
          "sumResolved": 1,
          "sumVolume": 100+Math.floor(Math.random() * 1000),
          "sumAnomaly": 0,
          "dateFirstFound": "2020-11-26",
          "avgHandlingTime": parseFloat(Math.floor(Math.random() * 55)+'.'+Math.floor(Math.random() * 50)),
          "avgResponseTime": parseFloat(Math.floor(Math.random() * 55)+'.'+Math.floor(Math.random() * 50)),
          "sumHandlingTime": 55.3,
          "sumResponseTime": 9.55,
          "sumPositivePercent": Math.floor(Math.random() * 100),
          "sumNegativePercent": Math.floor(Math.random() * 100),
          "sumNeutralPercent": "0.00"
        })
      }
      this.data.trend = trend.reverse();
    }
  },

  mounted() {
    //this.generateMockData();
    this.changeDateRange(this.viewType);
  },

  created() {
    this.wyChart = new WyChartModule();
  },


});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-metrics-chart {
  min-height: 200px;
  // box-shadow: 5px 5px 6px #0000001A;
  border-radius: 10px;
  // border: 1px solid #eee;
  background-color: #fff;
  padding: 20px 20px 2px 20px;
  margin-bottom: 25px;

  .wy-chart-grouping {
    .el-input__inner {
      width: 150px;
    }
  }

  .metrics-chart {
    width: 100%;
  }

  > table {
    width: 100%;
  }
  
  table.tr {
    vertical-align: baseline !important;
  }
  .title {
    font-size: 18px;
    font-weight: 500;
    line-height: 0;
    margin-left: 25px;
    color: #002852;
  }

  .chartTitle {
    font-size: 15px;
    font-weight: 500;
    color: #002852;   
  }

  .chartDate {
    font-size: 12px;
    margin-bottom: 8px;
    color: #002852;
  }

  .chartTraceLabel {
    font-size: 14px;
    text-align: center;

    div.chartTraceRow {
      margin-bottom: 130px;
      margin-top: 50px;
    }

    div.chartTraceRow {
      margin-bottom: 100px;
      margin-top: 10px;
    }

    div.chartTraceRow:last-child {
      margin-bottom: 20px;
    }

    .chartTracePercent {
      font-weight: bold;
      font-size: 16px;
      line-height: 14px;
      color: #002852;
    }
  }
  td.td-comparison-a {
    vertical-align: baseline;
  }
  td.td-comparison-a.single {
    .chartDate {
      padding-top: 110px;
      margin-bottom: 10px;
    }
  }

  td.td-comparison-b {
    vertical-align: baseline;
  }
  td.td-comparison-b.single {
    .chartDate {
      padding-top: 110px;
      margin-bottom: 10px;
    }
  } 

  .el-select:hover .el-input__inner {
    border-color: var(--site-font-primary-color);
  }
  .el-input.is-focus .el-input__inner {
    border-color: var(--site-font-primary-color);
  }
  .el-select .el-input__inner:focus {
    border-color: var(--site-font-primary-color);
  }
  input.el-input__inner {
    // border-width: 1px 1px 1px 1px !important;
    // border-radius: 10px;
    // padding-left: 13px;
    // padding-top: 2px;
    border-color: transparent;
    width: 130px;
    font-size: 16px;
    font-weight: 400;
    color: #002852;
  }
  .el-select .el-input .el-select__caret {
    background-image: url('../../assets/icons/icon_dropdown_arrow_down.png');
    background-repeat: no-repeat;
    background-size: 8px 5px;
    background-position-x: 4px;
    background-position-y: 12px;
    width: 20px;
    height: 20px;
    vertical-align: middle;
    transform: rotateZ(0);
  }
  .el-icon-arrow-up:before {
    content: " ";
  }

  .js-plotly-plot .plotly, .js-plotly-plot .plotly div {
    font-family: "Helvetica Neue",Helvetica,Arial,sans-serif;
  }

  .el-loading-mask {
    height: unset !important;
    opacity: unset;
    background-color: unset;
    height: 100% !important;
    .el-loading-spinner {
      top: 35%;
      background: var(--site-loading-icon);
      background-repeat: no-repeat;
      background-size: 25px;
      background-position: center;    
      height: fit-content !important;
    }
    .el-loading-spinner .circular {
      display: unset !important;
    }
  }
}
.line-chart-dropdown {
  .el-scrollbar__wrap {
    display: block !important;
  }
}
</style>